import { useCallback, useEffect, useState } from "react";

import { clearUnusedTags, deleteTag, listTags, renameTag, type TagRow } from "@/lib/setup";
import { useCollectionChanged } from "@/lib/useCollectionChanged";
import { useDialogs } from "@/components/shell/Dialogs";

import { Panel, PanelButton } from "./Setup";

/**
 * Tags, split into `App-managed` and `Yours`.
 *
 * The split is not decoration: **renaming one of these breaks something**, and the two directions
 * are opposite. A tag the app *writes* (`Enrich01`, `Freq:…`) comes back under its old name on the
 * next run, so the collection carries both. A tag the app only *reads* (`Prio`, applied by hand and
 * consumed by the frequency sort) breaks with no error at all — the sort just stops prioritising.
 * The server refuses either; this panel does not even offer it.
 *
 * `App-managed` rather than the handoff's `Job-managed`: no job touches `Prio` on the way in, and
 * `JMdictDivergent` is touched by no job at all.
 */
export function TagsPanel({
  onError,
  onSuccess,
}: {
  onError: (error: unknown) => void;
  onSuccess: () => void;
}) {
  const [tags, setTags] = useState<TagRow[]>([]);
  /** The tag being renamed in place, by name. */
  const [renaming, setRenaming] = useState<string | null>(null);
  const dialogs = useDialogs();

  const load = useCallback(() => {
    void listTags()
      .then(({ tags: rows }) => setTags(rows))
      .catch(onError);
  }, [onError]);

  useEffect(load, [load]);
  // Audio writes NoAudio / BadReading and Priority cleanup strips Prio, so a run started from
  // the card above changes both the list and its counts.
  useCollectionChanged(load);

  const run = async (action: () => Promise<{ tags: TagRow[] }>) => {
    try {
      setTags((await action()).tags);
      onSuccess();
    } catch (e) {
      onError(e);
    }
  };

  const machine = tags.filter((t) => t.machine);
  const mine = tags.filter((t) => !t.machine);

  /**
   * Renamed in the chip, not in a dialog.
   *
   * A chip is narrow, so the field is sized to its content (`size`) rather than left to collapse —
   * the same trick the reading tab's rename uses in a 34px strip. Offered on non-machine tags
   * only, exactly as the `✎` already was: an app-managed tag comes back doubled on a rename.
   *
   * The trim is sent, not merely validated. This checked `next.trim()` and then passed the raw
   * string, so a tag renamed with a trailing space kept it.
   */
  const commitRename = (tag: TagRow, raw: string) => {
    const next = raw.trim();
    setRenaming(null);
    if (next && next !== tag.name) void run(() => renameTag(tag.name, next));
  };

  const onDelete = async (tag: TagRow) => {
    const ok = await dialogs.confirm({
      title: `${tag.name} を削除？`,
      body: "ノート自体には触れません。",
      detail: `${tag.count.toLocaleString()}ノート`,
      confirmLabel: "タグを削除",
      tone: "danger",
    });
    if (ok) void run(() => deleteTag(tag.name));
  };

  return (
    <Panel
      title="タグ"
      subtitle={`使用中${tags.length} · タグをクリックで名前変更・削除`}
      actions={
        <PanelButton onClick={() => void run(() => clearUnusedTags())}>未使用を削除</PanelButton>
      }
    >
      <div className="flex flex-col gap-4 px-[18px] py-4">
        <TagGroup
          label="アプリ管理"
          note="アプリが読み書きするタグです。名前を変えると壊れます。"
          tags={machine}
          renaming={renaming}
          onBeginRename={setRenaming}
          onCommitRename={commitRename}
          onDelete={onDelete}
        />
        <TagGroup
          label="自分の"
          tags={mine}
          renaming={renaming}
          onBeginRename={setRenaming}
          onCommitRename={commitRename}
          onDelete={onDelete}
        />
      </div>
    </Panel>
  );
}

function TagGroup({
  label,
  note,
  tags,
  renaming,
  onBeginRename,
  onCommitRename,
  onDelete,
}: {
  label: string;
  note?: string;
  tags: TagRow[];
  /** The tag being renamed in place, by name. */
  renaming: string | null;
  onBeginRename: (name: string | null) => void;
  onCommitRename: (tag: TagRow, raw: string) => void;
  onDelete: (tag: TagRow) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-field-label font-semibold uppercase tracking-[.06em] text-ink-4">
          {label}
        </span>
        {note && <span className="text-meta text-ink-4">{note}</span>}
      </div>
      {tags.length === 0 ? (
        <span className="text-secondary text-ink-4">ありません。</span>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag.name}
              className={[
                "inline-flex items-center gap-1.5 rounded-[var(--r-4)] border px-[10px] py-[4px]",
                tag.machine ? "border-line bg-sunken" : "border-line-strong bg-void",
              ].join(" ")}
            >
              {renaming === tag.name ? (
                // `size` rather than a width: a chip is only as wide as its tag, so a field left to
                // its default would blow the row apart. Same trick the tab strip uses in 34px.
                <input
                  autoFocus
                  onFocus={(event) => event.currentTarget.select()}
                  defaultValue={tag.name}
                  size={Math.max(tag.name.length, 6)}
                  onKeyDown={(event) => {
                    // Tags carry Japanese too, and an IME's Enter commits a conversion — without
                    // this the rename fires on the half-typed value and rewrites the tag on every
                    // note holding it. Same guard as `AskPanel` and `TagChips`.
                    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
                    if (event.key === "Enter") onCommitRename(tag, event.currentTarget.value);
                    else if (event.key === "Escape") onBeginRename(null);
                  }}
                  onBlur={(event) => onCommitRename(tag, event.currentTarget.value)}
                  className="rounded-[var(--r-2)] border border-line-strong bg-void px-[4px] text-[14px]/[1.4] text-ink"
                />
              ) : (
                <span className="text-[14px]/[1.4] text-ink-2">{tag.name}</span>
              )}
              <span className="font-mono text-[14px]/[1.4] text-ink-4">{tag.count}</span>
              {/* Rename is withheld for machine tags rather than offered and rejected. */}
              {!tag.machine && renaming !== tag.name && (
                <button
                  type="button"
                  onClick={() => onBeginRename(tag.name)}
                  aria-label={`${tag.name} の名前を変更`}
                  className="text-[14px] text-ink-3 hover:text-accent"
                >
                  ✎
                </button>
              )}
              <button
                type="button"
                onClick={() => void onDelete(tag)}
                aria-label={`${tag.name} を削除`}
                className="text-[14px] text-ink-3 hover:text-accent-text"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
